<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div class="relative z-20" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-if="visible" @keyup.esc="hide">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                <div
                    class="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full sm:p-6"
                    :class="{'lg:w-6/12 xl:w-5/12 md:w-3/5 sm:w-11/12 xs:w-full' : !showComparison, 'lg:w-11/12 xl:w-4/5': showComparison}"
                >
                    <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                        <button type="button" class="btn-close" @click="cancel">
                            <span class="sr-only">{{ $t('shared.modals.close') }}</span>
                            <icon name="close"/>
                        </button>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="!showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h2 class="text-lg leading-6 font-bold text-gray-900">{{$t('shared.modals.adjust_quantity_modal.title')}}</h2>
                            <div class="mt-2 w-full py-5">
                                <form ref="adjustForm">
                                    <div class="flex w-full content-between relative">
                                        <table class="w-full p-3" style="font-family: 'Inter var';">
                                            <thead>
                                                <tr class="font-semibold">
                                                    <th class="py-3">&nbsp;</th>
                                                    <th>{{$t('shared.modals.adjust_quantity_modal.headline_current')}}</th>
                                                    <th>{{$t('shared.modals.adjust_quantity_modal.headline_new')}}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td>{{$t('shared.modals.adjust_quantity_modal.quantity_plan')}}</td>
                                                <td>{{ currentQuantity }}</td>
                                                <td class="w-1/5">
                                                    <input
                                                        ref="quantity"
                                                        type="number"
                                                        name="quantity"
                                                        id="quantity"
                                                        class="w-1/3"
                                                        :class="{'error' : !isQuantityValid()}"
                                                        :placeholder="form.quantity"
                                                        v-model="form.quantity"
                                                        :min="minQuantity"
                                                        @keyup="checkQuantity"
                                                    />
                                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                        <icon class="h-5 w-5 text-red-500" name="exclamation" v-if="!isQuantityValid()"/>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr class="text-gray-400">
                                                <td class="py-3">{{$t('shared.modals.adjust_quantity_modal.quantity_sold')}}</td>
                                                <td>{{ soldQuantity * -1 }}</td>
                                                <td>{{ soldQuantity * -1 }}</td>
                                            </tr>
                                            <tr class="text-gray-400">
                                                <td class="py-3">{{$t('shared.modals.adjust_quantity_modal.quantity_pending')}}</td>
                                                <td>{{ pendingQuantity * -1 }}</td>
                                                <td>{{ pendingQuantity * -1 }}</td>
                                            </tr>
                                            <tr class="border-t font-bold">
                                                <td class="py-3">{{$t('shared.modals.adjust_quantity_modal.quantity_available')}}</td>
                                                <td>{{ previewCurrentQuantity }}</td>
                                                <td>{{ previewNewQuantity }}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </form>
                            </div>
                            <alert-message
                                v-if="errors && errors.length > 0"
                                :error-type="ERROR_TYPE_ERROR"
                                :messages="errors"
                                :show-as-list="false"
                                :display-errors-title="false"
                                class="mb-5"
                            />
                            <alert-message
                                v-if="warnings && warnings.length > 0"
                                :error-type="ERROR_TYPE_WARNING"
                                :messages="warnings"
                                :show-as-list="false"
                                :display-errors-title="false"
                                class="mb-5"
                            />
                        </div>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h2 class="text-lg leading-6 font-bold text-gray-900">{{$t('shared.modals.adjust_quantity_modal.title')}}</h2>
                            <div class="mt-10 w-full flex">
                                <div class="pr-2" ref="titleBefore">
                                    <quantity-adjustment-overview
                                        :headline="this.$t('shared.modals.adjust_quantity_modal.adjust_overview.before_adjustment')"
                                        :quantity="this.currentQuantity"
                                        :sold="this.soldQuantity"
                                        :pending="this.pendingQuantity"
                                        :show-pending="true"
                                        :available="this.oldAvailable"
                                    />
                                </div>
                                <div ref="titleAfter">
                                    <quantity-adjustment-overview
                                        :headline="this.$t('shared.modals.adjust_quantity_modal.adjust_overview.after_adjustment')"
                                        :bold-headline="true"
                                        :quantity="this.newQuantity"
                                        :pending="this.pendingQuantity"
                                        :show-pending="true"
                                        :sold="this.newSold"
                                        :available="this.newAvailable"
                                    />
                                </div>
                            </div>
                            <div class="w-full">
                                <pyramid-levels-before-and-after
                                    :levels="this.filteredLevels"
                                    :forceHeaderSecondLine="true"
                                    ref="beforeAndAfter"
                                    @updated="resizeHandler"
                                />
                            </div>
                        </div>
                    </div>
                    <warning
                        class="mt-10 text-yellow-500 bg-orange-100"
                        :text="this.$t('shared.modals.adjust_quantity_modal.warning_adjust')"
                        v-if="showComparison && isAdjustmentNeeded()"
                    />
                    <warning class="mt-10 text-gray-500 bg-gray-100" :text="this.$t('shared.modals.warning')" v-if="showComparison"/>
                    <div class="mt-5 sm:mt-4 flex flex-row-reverse">
                        <button v-if="showComparison" type="button" class="btn btn-primary ml-5" @click="confirm">{{$t('shared.modals.confirm')}}</button>
                        <button v-if="!showComparison" type="button" class="btn btn-primary ml-5" @click="preview">{{$t('shared.modals.preview')}}</button>
                        <button v-if="showComparison" type="button" class="btn btn-secondary ml-5" @click="adjustQuantity">{{$t('shared.modals.adjust')}}</button>
                        <button type="button" class="btn btn-secondary" @click="cancel">{{$t('shared.modals.cancel')}}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import {ErrorTypes} from '../../mixins/ErrorTypes';
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import Warning from "../Alerts/Warning";
import QuantityAdjustmentOverview from "../Overviews/QuantityAdjustmentOverview";
import PyramidLevelsBeforeAndAfter from "../Tables/PyramidLevelsBeforeAndAfter";


export default {
    layout: Layout,
    components: {
        Icon,
        AlertMessage,
        QuantityAdjustmentOverview,
        PyramidLevelsBeforeAndAfter,
        Warning
    },
    mixins:[
        ErrorTypes
    ],
    props: {
        auction: Object,
        currentQuantity: {
            Type: Number,
            default: 0
        },
        minQuantity: Number,
        soldQuantity: Number,
        pendingQuantity: {
            type: Number,
            default: 0
        },
        activeLevel: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            visible: false,
            errors:  [],
            warnings: [],
            quantityIsWarning: false,
            showComparison: false,
            newQuantity: this.currentQuantity,

            levels: {
                type: Array,
                default: []
            },
            newLevels: [],
            oldLevels: [],

            oldPlan: {
                type: Number,
                default: 0
            },

            oldSold: {
                type: Number,
                default: 0
            },

            oldAvailable: {
                type: Number,
                default: 0
            },

            newSold: {
                type: Number,
                default: 0,
            },

            newAvailable: {
                type: Number,
                default: 0
            },

            form: {
                quantity: 0
            }
        }
    },
    methods: {
        resizeHandler() {
            if (this.$refs.titleBefore) {
                if (this.$refs.beforeAndAfter && this.$refs.beforeAndAfter) {
                    let dimensions = this.$refs.beforeAndAfter.getDimensions();
                    this.$refs.titleBefore.style.width = dimensions.left + 'px';
                    this.$refs.titleBefore.style.marginRight = dimensions.mid + 'px';
                    this.$refs.titleAfter.style.width = dimensions.right + 'px';
                }
            }
        },
        show() {
            this.errors = [];
            this.warnings = [];
            this.visible = true;
            this.form.quantity = this.currentQuantity;
            this.$nextTick(() => this.$refs.quantity.focus());
        },
        hide() {
            this.quantityIsWarning = false;
            this.showComparison = false;
            this.visible = false;
        },
        cancel() {
            this.hide();
            this.$emit('cancel');
            this.showComparison = false;
        },
        adjustQuantity() {
            if (true === this.showComparison) {
                this.showComparison = false;
                this.$nextTick(() => this.$refs.quantity.focus());
            }
        },
        preview() {
            let app = this;

            if (this.checkForm()) {
                this.newQuantity = parseInt(this.$refs.quantity.value);

                axios.post(route('auctions.adjust.quantity', { auction: this.auction.id, newQuantity: this.newQuantity, confirmed: 0 }))
                    .then((response) => {
                        app.levels = response.data.data.preview;
                        app.oldLevels = response.data.data.preview.old;
                        app.newLevels = response.data.data.preview.new;
                        app.showComparison = true;
                    })
                    .catch((error) => {
                        console.error(error);
                    });

                this.oldPlan = this.auction.article_quantity;
                this.oldSold = this.soldQuantity;
                this.oldAvailable = this.oldPlan - this.oldSold;

                this.newSold = this.soldQuantity;
                this.newAvailable = this.newQuantity - this.newSold;
            }
        },
        confirm() {
            let app = this;
            axios.post(route('auctions.adjust.quantity', { auction: this.auction.id, newQuantity: this.newQuantity, confirmed: 1 }))
                .then((response) => {
                    this.$emit('accept');
                    this.showComparison = false;
                    this.maxPriceIsValid = true;
                    this.errors = [];
                    this.visible = false;
                    app.$inertia.reload();
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        checkForm: function () {
            this.errors = this.checkQuantity();
            return this.errors.length === 0;
        },
        isQuantityValid() {
            return this.errors.length === 0;
        },
        checkQuantity() {
            this.errors = [];
            let ref = this.$refs.quantity;
            if(ref) {
                let quantity = ref.value
                if (quantity.length < 1) {
                    this.errors.push(this.$t('shared.modals.adjust_quantity_modal.quantity_errors.required'));
                }

                if (quantity.length > 0 && isNaN(parseInt(quantity))) {
                    this.errors.push(this.$t('shared.modals.adjust_quantity_modal.quantity_errors.number'));
                }

                if (parseInt(quantity) < this.minQuantity) {
                    this.errors.push(this.$t('shared.modals.adjust_quantity_modal.quantity_errors.too_low', {sold: this.minQuantity }));
                }
            }
            return this.errors;
        },
        isAdjustmentNeeded() {
            let retVal = false;
            if (this.newLevels) {
                this.newLevels.forEach((level) => {
                    if (level.adjusted === true) {
                        retVal = true;
                    }
                });
            }
            return retVal;
        }
    },
    computed: {
        filteredLevels() {
            let self = this;
            let retVal = [];
            let allowedStates = ['future'];
            let oldLevels = this.oldLevels.filter(lvl => allowedStates.includes(lvl.state) && lvl.sequence >= self.activeLevel.sequence);
            let newLevels = this.newLevels.filter(lvl => allowedStates.includes(lvl.state) && lvl.sequence >= self.activeLevel.sequence);
            let lvlCount = Math.max(oldLevels.length, newLevels.length);
            for( let i = 0; i < lvlCount; i++) {
                let lvl = {};
                // if old level exists
                if (oldLevels[i]) {
                    lvl.sequence = oldLevels[i].sequence;
                    lvl.price_drop = oldLevels[i].price_drop;
                    lvl.fixed_time = oldLevels[i].fixed_time;
                    lvl.price = oldLevels[i].price;
                    lvl.quantity = oldLevels[i].quantity;
                } else {
                    lvl.sequence = '';
                    lvl.price_drop = 0;
                    lvl.fixed_time = '';
                    lvl.price = null;
                    lvl.quantity = '';
                }

                // if new level exists
                if (newLevels[i]) {
                    lvl.new_sequence = newLevels[i].sequence;
                    lvl.new_price_drop = newLevels[i].price_drop;
                    lvl.new_fixed_time = newLevels[i].fixed_time;
                    lvl.new_price = newLevels[i].price;
                    lvl.new_quantity = newLevels[i].quantity;
                    lvl.adjusted = newLevels[i].adjusted;
                } else {
                    lvl.new_sequence = '';
                    lvl.new_price_drop = 0;
                    lvl.new_fixed_time = '';
                    lvl.new_price = null;
                    lvl.new_quantity = '';
                    lvl.adjusted = false;
                }
                retVal.push(lvl);
            }
            return retVal;
        },
        previewCurrentQuantity() {
            return this.currentQuantity - this.soldQuantity - this.pendingQuantity;
        },
        previewNewQuantity() {
            return this.form.quantity - this.soldQuantity - this.pendingQuantity;
        }
    }
}
</script>
